import React, { useRef, useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';

interface FeaturedCollectionProps {
  onNavigate: (page: string) => void;
}

const FeaturedCollection: React.FC<FeaturedCollectionProps> = ({ onNavigate }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-stone-50 relative overflow-hidden">
      {/* Background Texture */}
      <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none"></div>
      
      {/* Decorative Line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-24 bg-gold-200"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          
          {/* Image Side */}
          <div className={`lg:w-3/5 w-full relative group transition-all duration-1000 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'}`}>
            <div className="absolute -inset-4 border border-gold-200 translate-x-4 translate-y-4 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
            <div className="relative overflow-hidden aspect-[4/3] shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop" 
                alt="Signature Jewelry" 
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur px-4 py-2 shadow-sm">
                 <span className="text-[10px] tracking-[0.2em] uppercase text-stone-900 font-bold">Signature Pieces</span>
              </div>
            </div>
          </div>

          {/* Text Side */}
          <div className={`lg:w-2/5 w-full text-center lg:text-left transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}>
            <span className="text-gold-600 text-xs tracking-[0.25em] uppercase mb-4 block">Refined Elegance</span>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mb-6 leading-tight">
              Jewelry That Holds <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-gold-700">Attention</span>
            </h2>
            <p className="text-stone-600 font-light mb-8 leading-relaxed">
              Explore standout pieces with clean product pages, wishlist functionality, and bag actions built in. Each piece tells a story of craftsmanship and heritage.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button 
                onClick={() => onNavigate('collections')}
                className="bg-stone-900 text-white px-8 py-3 text-xs tracking-[0.2em] uppercase hover:bg-gold-600 transition-colors shadow-lg shadow-stone-200"
              >
                Shop Jewelry
              </button>
              <button 
                onClick={() => onNavigate('shop')}
                className="border border-stone-300 text-stone-600 px-8 py-3 text-xs tracking-[0.2em] uppercase hover:border-gold-500 hover:text-gold-600 transition-colors flex items-center gap-2 justify-center"
              >
                View All <ArrowUpRight size={14} />
              </button>
            </div>

            {/* Features Mini-Grid */}
            <div className="mt-12 grid grid-cols-3 gap-4 border-t border-stone-200 pt-8">
              {['Curated', 'Authentic', 'Seamless'].map((item) => (
                <div key={item} className="text-center lg:text-left">
                  <h4 className="text-gold-700 text-xs uppercase tracking-widest mb-1">{item}</h4>
                  <span className="text-[10px] text-stone-500">Premium Standard</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FeaturedCollection;